import React from "react";
import { Link } from "react-router-dom";

const Privacy = () => {
    return (
        <>
            <div className="pt-10 pb-10 bg-black text-gray-400">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <h1 className="text-2xl sm:text-3xl font-bold mb-8 text-green">Privacy Policy for Game Gems</h1>
                    <p className="mb-4"><strong>Effective Date:</strong> July 30, 2025</p>

                    <p className="mb-6">
                        Welcome to <strong>Game Gems</strong>! Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2 text-white">1. Information We Collect</h2>

                    <h3 className="text-lg font-semibold mt-4 mb-1 text-gray-200">a. Personal Information</h3>
                    <p className="mb-4">We do <strong>not</strong> collect personally identifiable information (PII) unless provided voluntarily (e.g., feedback forms or login).</p>

                    <h3 className="text-lg font-semibold mb-1 text-gray-200">b. Non-Personal Information</h3>
                    <p className="mb-2">We may automatically collect:</p>
                    <ul className="list-disc list-inside mb-4 space-y-1">
                        <li>Device info (model, OS, ID)</li>
                        <li>Usage data (clicks, in-app events)</li>
                        <li>Crash logs and diagnostics</li>
                    </ul>

                    <h3 className="text-lg font-semibold mb-1 text-gray-200">c. Third-Party Data</h3>
                    <p className="mb-6">
                        If you sign in via third-party services (e.g., Google Play Games), we may access limited info (e.g., username, avatar) as allowed by that service.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2 text-white">2. How We Use Your Information</h2>
                    <ul className="list-disc list-inside mb-6 space-y-1">
                        <li>Improve performance and experience</li>
                        <li>Personalize features/content</li>
                        <li>Provide support & updates</li>
                        <li>Prevent fraud or abuse</li>
                    </ul>
                    <p className="mb-6 font-semibold text-green-600">We do not sell your data.</p>

                    <h2 className="text-2xl font-semibold mb-2 text-white">3. Third-Party Services</h2>
                    <ul className="list-disc list-inside mb-6 space-y-1">
                        <li><Link to="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-green underline">Google Play Services</Link></li>
                        <li><Link to="https://support.google.com/admob/answer/6128543" target="_blank" rel="noreferrer" className="text-green underline">AdMob</Link></li>
                        <li><Link to="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" className="text-green underline">Firebase Analytics & Crashlytics</Link></li>
                    </ul>

                    <h2 className="text-2xl font-semibold mb-2 text-white">4. Data Retention</h2>
                    <p className="mb-4">We keep your data only as long as needed for the purposes outlined.</p>
                    <p className="mb-6">You can request deletion anytime at: <Link to="https://gamegems.co/delete.html" target="_blank" rel="noreferrer" className="text-green underline">gamegems.co/delete.html</Link></p>

                    <h2 className="text-2xl font-semibold mb-2 text-white">5. Children’s Privacy</h2>
                    <p className="mb-6">Game Gems is <strong>not for children under 13</strong>. We don’t knowingly collect their data. If we do, it will be deleted.</p>

                    <h2 className="text-2xl font-semibold mb-2 text-white">6. Security</h2>
                    <p className="mb-6">We use encryption and secure servers. No method is 100% secure, but we do our best to protect your data.</p>

                    <h2 className="text-2xl font-semibold mb-2 text-white">7. Your Rights</h2>
                    <ul className="list-disc list-inside mb-6 space-y-1">
                        <li>Access or correct your info</li>
                        <li>Request deletion</li>
                        <li>Withdraw consent</li>
                    </ul>

                    <h2 className="text-2xl font-semibold mb-2 text-white">8. Changes to This Policy</h2>
                    <p className="mb-6">We may update this Privacy Policy. You’ll be notified of major changes via in-app alerts or updates.</p>

                    <h2 className="text-2xl font-semibold mb-2 text-white">9. SDKs & Open Source Licenses</h2>
                    <div className="overflow-auto bg-black p-4 rounded-md mb-6">
                        <table className="w-full table-auto text-md">
                            <thead>
                                <tr className="text-left text-gray-200 border-b border-gray-700">
                                    <th className="py-2">SDK / Plugin</th>
                                    <th className="py-2">License</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-400">
                                {[
                                    ['firebase_core', 'BSD-3-Clause'],
                                    ['firebase_auth', 'BSD-3-Clause'],
                                    ['firebase_messaging', 'BSD-3-Clause'],
                                    ['google_sign_in', 'BSD'],
                                    ['flutter_udid', 'MIT'],
                                    ['dio', 'MIT'],
                                    ['get', 'MIT'],
                                    ['get_storage', 'MIT'],
                                    ['get_cli', 'MIT'],
                                    ['url_launcher', 'BSD'],
                                    ['awesome_notifications', 'MIT'],
                                    ['permission_handler', 'BSD'],
                                    ['internet_connection_checker_plus', 'MIT'],
                                    ['webview_flutter', 'BSD'],
                                    ['flutter_dotenv', 'MIT'],
                                    ['shimmer', 'MIT'],
                                    ['share_plus', 'BSD'],
                                    ['animate_do', 'MIT'],
                                    ['flutter_spinkit', 'MIT'],
                                    ['modal_progress_hud_nsn', 'MIT'],
                                    ['flutter_svg', 'MIT'],
                                    ['cupertino_icons', 'MIT']
                                ].map(([sdk, license], i) => (
                                    <tr key={i} className="border-b border-gray-700">
                                        <td className="py-2">{sdk}</td>
                                        <td className="py-2 font-medium">{license}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="mb-6">We ensure compliance with all open-source and Google Play SDK policies.</p>

                    <h2 className="text-2xl font-semibold mb-2">10. Contact Us</h2>
                    <p className="mb-2">📧 <strong>Email:</strong> <a href="mailto:gamegems.help@gmail.com" className="text-green underline">gamegems.help@gmail.com</a></p>
                    <p>🌐 <strong>Website:</strong> <a href="https://gamegems.co" target="_blank" rel="noreferrer" className="text-green underline">gamegems.co</a></p>
                </div>
            </div>
        </>
    )
}

export default Privacy;